import isEqual from "lodash/isEqual";
import i18n from "@viz-ui/i18n/i18n";
import { isRowChecked, getRowDataId } from "./rowSelectionHelper";
import GlobalValueFormatter from "../../services/formatters/globalValueFormatter";
import ConditionalFormatting from "../../services/quickMenu/conditionalFormat/conditionalFormattingService";
import logger from "../../services/logger/logger";
const blankKey = "(blank)";

function transformHotData(props, rowSelection) {
  const { data, fields, fieldFormatIdentifier, skipCellValueFormatting, skipFormattingOnCells, checkedRowIds } = props;
  logger.log(`${fieldFormatIdentifier} - HoT data transformer`);
  logger.log(`${fieldFormatIdentifier} - fields length: ${fields ? fields.length : 0}`);
  logger.log(`${fieldFormatIdentifier} - data length: ${data ? data.length : 0}`);

  if (!fields || fields.length === 0) {
    logger.log(`${fieldFormatIdentifier} - No fields provided, returning empty array`);
    return [];
  }

  const fieldModels = fields;

  const conditionalFormattersCache = getConditionalFormatters(fieldModels, fieldFormatIdentifier);

  if (!data || !Array.isArray(data)) {
    logger.log(`${fieldFormatIdentifier} - No data provided or data is not an array, returning empty array`);
    return [];
  }

  const effectiveCheckedRowIds = checkedRowIds || rowSelection.getCheckedRowIds();

  let transformedData = data.map((dataRow, rowIndex) => {
    let rowData;
    if (dataRow.key) {
      try {
        rowData = JSON.parse(dataRow.key);
        rowData = { ...rowData, ...dataRow.values };
      } catch (error) {
        console.error(`Error parsing key for row ${rowIndex}:`, error);
        rowData = { ...dataRow.values };
      }
    } else {
      rowData = { ...dataRow };
    }

    let transformedRow = Object.assign({}, rowData);

    transformedRow = Object.assign(
      transformedRow,
      getRowSelectionValue(props, effectiveCheckedRowIds, rowData)
    );
    transformedRow = Object.assign(transformedRow, getRowCommentValue(props, rowData));
    fieldModels.forEach(fieldModel => {
      transformedRow = Object.assign(
        transformedRow,
        getRowFieldValue(
          rowData,
          rowIndex,
          fieldModel,
          fieldFormatIdentifier,
          skipCellValueFormatting,
          skipFormattingOnCells,
          conditionalFormattersCache
        )
      );
    });

    return transformedRow;
  });

  transformedData = getFilteredRows(props, transformedData);

  logger.log(`${fieldFormatIdentifier} - transformedData length: ${transformedData.length}`);
  return transformedData;
}

// Returns true when the ONLY change is checkedRowIds or rowSelectInverse (checkbox state),
// AND showCheckedRowsOnly is NOT active. When showCheckedRowsOnly is true, checkbox changes
// alter which rows are visible, requiring a full table rebuild (hot.loadData).
// When this returns true, we can skip the expensive full transformHotData + updateHandsontable
// cycle and instead do a lightweight in-place update of __checked values.
function isCheckboxOnlyChange(currProps, nextProps, rowSelection) {
  // In bulk mode (showCheckedRowsOnly), checkbox changes alter visible rows,
  // so a full transform + table update is required.
  if (nextProps.showCheckedRowsOnly) {
    return false;
  }

  const hasCheckboxChange =
    !isEqual(currProps.checkedRowIds, nextProps.checkedRowIds) ||
    currProps.rowSelectInverse !== nextProps.rowSelectInverse ||
    !isEqual(currProps.checkedRowIds, rowSelection.getCheckedRowIds());

  const hasOtherChange =
    currProps.fields !== nextProps.fields ||
    currProps.fieldFormatIdentifier !== nextProps.fieldFormatIdentifier ||
    currProps.data !== nextProps.data ||
    currProps.showCheckedRowsOnly !== nextProps.showCheckedRowsOnly ||
    currProps.commentCounts !== nextProps.commentCounts;

  return hasCheckboxChange && !hasOtherChange;
}

function shouldTransformData(currProps, nextProps, rowSelection) {
  return (
    currProps.fields !== nextProps.fields ||
    currProps.fieldFormatIdentifier !== nextProps.fieldFormatIdentifier ||
    currProps.data !== nextProps.data ||
    currProps.showCheckedRowsOnly !== nextProps.showCheckedRowsOnly ||
    currProps.commentCounts !== nextProps.commentCounts ||
    !isEqual(currProps.checkedRowIds, nextProps.checkedRowIds) ||
    currProps.rowSelectInverse !== nextProps.rowSelectInverse ||
    !isEqual(currProps.checkedRowIds, rowSelection.getCheckedRowIds())
  );
}

function shouldUpdateTable(currProps, nextProps, rowSelection) {
  return (
    shouldTransformData(currProps, nextProps, rowSelection) ||
    !isEqual(currProps.columnConfigs, nextProps.columnConfigs)
  );
}

// Lightweight in-place update of __checked values in existing transformedData.
// Avoids the O(rows x fields) cost of full transformHotData by only updating
// the __checked property and re-running the showCheckedRowsOnly filter.
function updateCheckedState(props, transformedData) {
  const checkedRowIds = props.checkedRowIds || [];

  const updatedData = transformedData.map(row => ({
    ...row,
    __checked: isRowChecked(props, checkedRowIds, row),
  }));

  return getFilteredRows(props, updatedData);
}

function getRowSelectionValue(props, checkedRowIds, rowData) {
  return {
    __checked: isRowChecked(props, checkedRowIds, rowData),
  };
}

function getRowCommentValue(props, rowData) {
  return {
    __comment: getCommentIconClassAndDescription(props, rowData),
  };
}

function getConditionalFormatters(fieldModels, fieldFormatIdentifier) {
  if (!fieldFormatIdentifier) return {};

  const formattersCache = {};
  fieldModels.forEach(fieldModel => {
    let fieldName;
    try {
      fieldName = fieldModel.name();
    } catch (error) {
      fieldName = fieldModel._data && fieldModel._data.name;
    }

    if (fieldName) {
      formattersCache[fieldName] = {
        textColor: ConditionalFormatting.getTextColorFormatter(fieldName, fieldFormatIdentifier),
        backgroundColor: ConditionalFormatting.getBackgroundColorFormatter(fieldName, fieldFormatIdentifier),
        iconColor: ConditionalFormatting.getIconColorFormatter(fieldName, fieldFormatIdentifier),
        iconName: ConditionalFormatting.getIconNameFormatter(fieldName, fieldFormatIdentifier),
      };
    }
  });

  return formattersCache;
}

function getRowFieldValue(
  rowData,
  rowIndex,
  fieldModel,
  fieldFormatIdentifier,
  skipCellValueFormatting,
  skipFormattingOnCells,
  conditionalFormattersCache
) {
  let fieldName, fieldType;
  try {
    fieldName = fieldModel.name();
    fieldType = fieldModel.type();
  } catch (error) {
    fieldName = fieldModel._data && fieldModel._data.name;
    fieldType = fieldModel._data && fieldModel._data.type;
  }
  const fieldValueObj = {};
  fieldValueObj[fieldName] = {};
  if (Object.hasOwnProperty.call(rowData, fieldName)) {
    const rawFieldValue = rowData[fieldName];
    if (typeof rawFieldValue === "string") {
      fieldValueObj[fieldName] = {
        value: rawFieldValue,
        formattedValue: rawFieldValue,
      };
    } else if (rawFieldValue && typeof rawFieldValue === "object") {
      fieldValueObj[fieldName] = Object.assign(fieldValueObj[fieldName], rawFieldValue);
    } else {
      fieldValueObj[fieldName] = {
        value: rawFieldValue,
        formattedValue: String(rawFieldValue || ""),
      };
    }

    let fieldValue = fieldValueObj[fieldName].value;
    const shouldSkipFormattingOnCell =
      isLogicalBlankValue(fieldValueObj, fieldName, fieldType) ||
      isValueBlank(fieldValue) ||
      getShouldSkipFormattingOnCell(rowIndex, fieldName, skipFormattingOnCells);
    if (isFieldTypeDate(fieldType) && isDateValueInvalid(fieldValue)) {
      fieldValue = "Invalid date";
    }
    if (!skipCellValueFormatting && !shouldSkipFormattingOnCell) {
      fieldValueObj[fieldName].formattedValue = GlobalValueFormatter.formatValue(
        fieldValue,
        fieldFormatIdentifier,
        fieldName
      );
    }

    // Apply conditional formatting using cached formatters
    if (
      fieldFormatIdentifier &&
      fieldValue !== undefined &&
      fieldValue !== null &&
      conditionalFormattersCache[fieldName]
    ) {
      const formatters = conditionalFormattersCache[fieldName];

      const textColor = formatters.textColor(fieldValue);
      const backgroundColor = formatters.backgroundColor(fieldValue);
      const iconColor = formatters.iconColor(fieldValue);
      const iconName = formatters.iconName(fieldValue);

      if (textColor) fieldValueObj[fieldName].textColor = textColor;
      if (backgroundColor) fieldValueObj[fieldName].backgroundColor = backgroundColor;
      if (iconColor) fieldValueObj[fieldName].iconColor = iconColor;
      if (iconName) fieldValueObj[fieldName].iconName = iconName;
    }
  } else {
    fieldValueObj[fieldName].value = null;
  }

  fieldValueObj[fieldName].toString = function() {
    return this.formattedValue || this.value;
  };

  return fieldValueObj;
}

function getShouldSkipFormattingOnCell(rowIndex, fieldName, skipFormattingOnCells) {
  return skipFormattingOnCells.findIndex(cellToSkip => isEqual(cellToSkip, { rowIndex, fieldName })) > -1;
}

function isValueBlank(value) {
  return value === blankKey;
}

function isDateValueInvalid(value) {
  let valueArr = value && value.split("-");
  if (valueArr && valueArr.length == 1) valueArr = value.split("/");
  return value && (valueArr.length <= 2 || valueArr.filter(val => isNaN(parseInt(val))).length > 1) ? true : false;
}

function isFieldTypeDate(type) {
  return type === "date" || type === "D" || type === "dateTime" || type === "datetime" || type === "DT";
}

function isLogicalBlankValue(fieldValueObj, fieldName, type) {
  return type === "logical" && !fieldValueObj[fieldName].value;
}

function getFilteredRows(props, data) {
  if (props.showCheckedRowsOnly && props.isRowSelectionEnabled) {
    return data.filter(rowData => rowData.__checked);
  }
  return data;
}

function getCommentIconClassAndDescription(props, rowData) {
  const { commentCounts, rowIdKey } = props;
  const rowDataId = getRowDataId(rowData, rowIdKey);
  if (commentCounts && rowDataId) {
    const comment = commentCounts.find(item => item.id === rowDataId);
    if (comment) {
      const attachmentCount = comment.attachments_count;
      const commentCount = comment.comments_count;

      if (commentCount > 0 && attachmentCount > 0) {
        return {
          iconClass: "acl-i-comment-attachment",
          description: i18n.t("_DataGrid.HasCommentsAndAttachments_"),
        };
      }
      if (commentCount > 0) {
        return {
          iconClass: "acl-i-comment",
          description: i18n.t("_DataGrid.HasComments_"),
        };
      }
    }
  }

  return { iconClass: "", description: "" };
}

export { transformHotData, shouldTransformData, shouldUpdateTable, isCheckboxOnlyChange, updateCheckedState, isDateValueInvalid };
